/* eslint-disable @typescript-eslint/no-explicit-any */

import { OPMEvidenceData } from "@/api/opm-evidence";
import { OPMFATData } from "@/api/opm-fat";
import { OPMFDTData } from "@/api/opm-fdt";
import { OPMPoleData } from "@/api/opm-pole";

// Setiap fungsi menerima data aplikasi dan mengembalikan boolean (valid/tidak)
type ValidationFunction = (data: any) => boolean | null;
type ValidationDataOPMFAT = {
  opmFats: OPMFATData[] | undefined;
  abdFatCount: number | undefined;
};

// BE kadang gagal generate OPM FDT/FAT walau 1 RFS seharusnya selalu punya
// datanya, jadi array kosong berarti "belum ada data" (invalid), bukan
// "semua sudah valid" ([].every(...) === true secara vakum).
type FdtFlagKey =
  | "poleFdtStatus"
  | "imageFdtStatus"
  | "splitterFdtStatus"
  | "inputFdtStatus"
  | "outputFdtStatus";

function everyFdt(
  opmFdts: OPMFDTData[] | undefined,
  key: FdtFlagKey,
): boolean | null {
  if (!opmFdts) return null; // masih loading / belum ada data sama sekali
  if (opmFdts.length === 0) return false; // BE gagal generate FDT
  return opmFdts.every((fdt) => !!fdt[key]);
}

type FatFlagKey =
  | "poleFatStatus"
  | "imageFatStatus"
  | "outputFatStatus"
  | "splitterFatStatus";

function everyFat(
  opmFats: OPMFATData[] | undefined,
  abdFatCount: number | undefined,
  key: FatFlagKey,
): boolean {
  if (!abdFatCount || !opmFats || opmFats.length === 0) return false;
  const isFull = opmFats.length === abdFatCount;
  const isFullyValid = opmFats.every((fat) => !!fat[key]);
  return isFull && isFullyValid;
}

function getPoleFDTStatus(opmFdts?: OPMFDTData[]) {
  return everyFdt(opmFdts, "poleFdtStatus");
}

function getImageFDTStatus(opmFdts?: OPMFDTData[]) {
  return everyFdt(opmFdts, "imageFdtStatus");
}

function getSplitterFDTStatus(opmFdts?: OPMFDTData[]) {
  return everyFdt(opmFdts, "splitterFdtStatus");
}

function getPoleFATStatus(opmFats?: OPMFATData[], abdFatCount?: number) {
  return everyFat(opmFats, abdFatCount, "poleFatStatus");
}

function getImageFATStatus(opmFats?: OPMFATData[], abdFatCount?: number) {
  return everyFat(opmFats, abdFatCount, "imageFatStatus");
}

function getOutputFATStatus(opmFats?: OPMFATData[], abdFatCount?: number) {
  return everyFat(opmFats, abdFatCount, "outputFatStatus");
}

function getSplitterFATStatus(opmFats?: OPMFATData[], abdFatCount?: number) {
  return everyFat(opmFats, abdFatCount, "splitterFatStatus");
}

// Ambil satu record OPM Pole terbaru per poleId (API bisa mengembalikan
// beberapa record untuk poleId yang sama, mis. sisa record lama sebelum
// re-submit).
function getLatestPolesByPoleId(opmPoles?: OPMPoleData[]) {
  const latestByPoleId = new Map<string, OPMPoleData>();
  for (const pole of opmPoles ?? []) {
    const existing = latestByPoleId.get(pole.poleId);
    if (
      !existing ||
      new Date(pole.updatedDate ?? pole.createdDate).getTime() >
        new Date(existing.updatedDate ?? existing.createdDate).getTime()
    ) {
      latestByPoleId.set(pole.poleId, pole);
    }
  }
  return Array.from(latestByPoleId.values());
}

function getPoleEntityStatus(opmPoles?: OPMPoleData[], abdPoleCount?: number) {
  const latestPoles = getLatestPolesByPoleId(opmPoles);
  const isFull = latestPoles.length === (abdPoleCount ?? 0);
  const isFullyValid = latestPoles.every((pole) => pole.isValid);

  return !!(isFull && isFullyValid);
}

// Peta (Map) dari 'code' ke fungsi validasinya
export const validationStrategies: Record<string, ValidationFunction> = {
  A00: () => {
    return true;
  },
  A01: (data: OPMFDTData[] | undefined) => {
    // FDT Pole is uploaded
    return getPoleFDTStatus(data);
  },
  A02: (data: OPMFDTData[] | undefined) => {
    // FDT Photo is uploaded
    return getImageFDTStatus(data);
  },
  A03: (data: OPMFDTData[] | undefined) => {
    // FDT Input is uploaded
    return everyFdt(data, "inputFdtStatus");
  },
  A04: (data: OPMFDTData[] | undefined) => {
    // FDT Output is uploaded
    return everyFdt(data, "outputFdtStatus");
  },
  A05: (data: ValidationDataOPMFAT) => {
    // All FAT Pole is uploaded
    const { opmFats, abdFatCount } = data;
    return getPoleFATStatus(opmFats, abdFatCount);
  },
  A06: (data: ValidationDataOPMFAT) => {
    // All FAT Photo is uploaded
    const { opmFats, abdFatCount } = data;
    return getImageFATStatus(opmFats, abdFatCount);
  },
  A07: (data: ValidationDataOPMFAT) => {
    // All FAT Output is uploaded
    const { opmFats, abdFatCount } = data;
    return getOutputFATStatus(opmFats, abdFatCount);
  },
  A08: (data: {
    opmFats: OPMFATData[] | undefined;
    abdFatCount: number | undefined;
    opmFdts: OPMFDTData[] | undefined;
    opmPoles: OPMPoleData[] | undefined;
    abdPoleCount: number | undefined;
  }) => {
    // All Pole is uploaded
    const { opmFats, abdFatCount, opmFdts, opmPoles, abdPoleCount } = data;
    return (
      getPoleFATStatus(opmFats, abdFatCount) &&
      getPoleFDTStatus(opmFdts) &&
      getPoleEntityStatus(opmPoles, abdPoleCount)
    );
  },
  A09: (data: {
    opmFats: OPMFATData[] | undefined;
    abdFatCount: number | undefined;
    opmFdts: OPMFDTData[] | undefined;
  }) => {
    // Foto harus punya metadata. Splitter ikut dihitung di sini: foto splitter
    // FAT/FDT juga wajib bermetadata, dan tidak ada rule lain yang membacanya.
    const { opmFats, abdFatCount, opmFdts } = data;
    return (
      getImageFATStatus(opmFats, abdFatCount) &&
      getImageFDTStatus(opmFdts) &&
      getSplitterFATStatus(opmFats, abdFatCount) &&
      getSplitterFDTStatus(opmFdts)
    );
  },
  A10: (data: OPMFDTData[] | undefined) => {
    // FDT Photo is validated using OCR
    return getImageFDTStatus(data);
  },
  A11: (data: ValidationDataOPMFAT) => {
    // FAT Photo is validated using OCR
    const { opmFats, abdFatCount } = data;
    return getImageFATStatus(opmFats, abdFatCount);
  },
  A12: (data: ValidationDataOPMFAT) => {
    // All FAT Output match the master param range
    const { opmFats, abdFatCount } = data;
    return getOutputFATStatus(opmFats, abdFatCount);
  },
  A13: (data: OPMEvidenceData[]) => {
    // OLT Evidence is required
    const oltEvidence = data?.find(
      (evidence) => evidence.opmEvidenceType === "olt",
    );
    return oltEvidence?.isValid ?? false;
  },
  A14: (data: OPMEvidenceData[]) => {
    // Feeder Evidence Is Required
    const oltEvidence = data?.find(
      (evidence) => evidence.opmEvidenceType === "feeder",
    );
    return oltEvidence?.isValid ?? false;
  },
  A15: (data: OPMEvidenceData[]) => {
    // Activity Evidence Is Required
    const oltEvidence = data?.find(
      (evidence) => evidence.opmEvidenceType === "activity",
    );
    return oltEvidence?.isValid ?? false;
  },
  A16: (data: OPMEvidenceData[]) => {
    // Clamp Hook Evidence Is Required
    const oltEvidence = data?.find(
      (evidence) => evidence.opmEvidenceType === "clamphook",
    );
    return oltEvidence?.isValid ?? false;
  },
};

// Fungsi fallback untuk kode yang belum diimplementasikan
export const defaultValidator: ValidationFunction = () => false;
